import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { LIST_ORGANIZATIONS, CHANGE_ORG_PLAN } from '../graphql/admin';
import { AdminLayout } from '../components/AdminLayout';

export function AdminOrganizationsPage() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlan, setFilterPlan] = useState<string>('');
  const [filterPromo, setFilterPromo] = useState<string>('');

  const { data, loading, refetch } = useQuery(LIST_ORGANIZATIONS, {
    variables: {
      filters: {
        page,
        pageSize,
        searchQuery: searchQuery || undefined,
        plan: filterPlan || undefined,
        isPromoAccount: filterPromo === 'promo' ? true : filterPromo === 'paid' ? false : undefined,
      },
    },
  });

  const [changeOrgPlan] = useMutation(CHANGE_ORG_PLAN, {
    onCompleted: () => {
      refetch();
    },
  });

  const organizations = data?.adminListOrganizations?.organizations || [];
  const total = data?.adminListOrganizations?.total || 0;
  const totalPages = data?.adminListOrganizations?.totalPages || 1;

  const handleChangePlan = async (organizationId: string, newPlan: string) => {
    if (window.confirm(`Change organization plan to ${newPlan.toUpperCase()}?`)) {
      await changeOrgPlan({
        variables: {
          organizationId,
          newPlan,
        },
      });
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Organization Management</h1>
          <p className="text-gray-600 mt-1">
            Total organizations: {total.toLocaleString()}
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Name or slug..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Plan Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan
                </label>
                <select
                  value={filterPlan}
                  onChange={(e) => {
                    setFilterPlan(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Plans</option>
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              {/* Promo Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Type
                </label>
                <select
                  value={filterPromo}
                  onChange={(e) => {
                    setFilterPromo(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="paid">Paying</option>
                  <option value="promo">Promo Code</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Search
              </button>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterPlan('');
                  setFilterPromo('');
                  setPage(1);
                  refetch();
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Organizations Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-pulse">Loading organizations...</div>
            </div>
          ) : organizations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No organizations found matching your criteria
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Organization
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Plan
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Members
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Usage
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        MRR
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Created
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizations.map((org: any) => (
                      <tr key={org.organizationId} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium text-gray-900">{org.name}</div>
                            <div className="text-sm text-gray-600">/{org.slug}</div>
                            {org.isPromoAccount && (
                              <div className="text-xs text-purple-600 mt-1">
                                Promo: #{org.promoCodeId}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              org.plan === 'enterprise'
                                ? 'bg-purple-100 text-purple-800'
                                : org.plan === 'pro'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {org.plan.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {org.memberCount} / {org.maxMembers}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm text-gray-600">
                            <div>
                              {org.contextCount} / {org.maxContexts} contexts
                            </div>
                            <div>
                              {org.projectCount} / {org.maxProjects} projects
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900">
                            {formatCurrency(org.mrrCents)}
                          </div>
                          {org.stripeSubscriptionId && (
                            <div className="text-xs text-gray-500">
                              Stripe: {org.stripeSubscriptionId.substring(0, 12)}...
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDate(org.createdAt)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => {
                                // TODO: Implement org details modal
                                alert(`View details for ${org.name}`);
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm text-left"
                            >
                              View Details
                            </button>
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleChangePlan(org.organizationId, e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              className="text-sm border border-gray-300 rounded px-2 py-1 text-gray-700"
                            >
                              <option value="">Change Plan...</option>
                              {org.plan !== 'free' && <option value="free">Free</option>}
                              {org.plan !== 'pro' && <option value="pro">Pro</option>}
                              {org.plan !== 'enterprise' && (
                                <option value="enterprise">Enterprise</option>
                              )}
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t">
                <div className="text-sm text-gray-700">
                  Showing {(page - 1) * pageSize + 1} to{' '}
                  {Math.min(page * pageSize, total)} of {total} organizations
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`px-3 py-1 rounded-md text-sm ${
                            page === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
